import { useEffect, useState, useMemo, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faDownload, faChevronDown, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../../shared/utils/cn'
import { ExportDateRange } from './ExportDateRange'
import { ExportSensorSelection } from './ExportSensorSelection'
import { ExportSummary } from './ExportSummary'
import { ExportProgress } from './ExportProgress'
import { ExportDeviceSelection } from './ExportDeviceSelection'
import { ExportMultiProgress } from './ExportMultiProgress'
import {
  ExportModalProps,
  ExportSensorConfig,
  ExportSensorSelection as SensorSelection,
  ExportProgress as ProgressState,
  CsvColumn,
  TimestampFormat,
  TIMESTAMP_FORMATS,
  MultiExportProgress,
  DeviceExportStatus,
  FlowMeterMeta,
} from '../../utils/csvExportTypes'
import {
  generateDefaultFilename,
  generateCsvExport,
  generateColumnHeader,
  transformSensorData,
  downloadCsvFile,
  estimateReadingCount,
  isRainfallProduct,
  isFlowMeterProduct,
} from '../../utils/csvExport'
import { useAccountContext } from '../../contexts'
import { UnitCategoryId } from '../../common/enums'
import { parseHardwareVersion, parseFirmwareVersion } from '../../common/utils'
import { useDeviceReadings, useBatteryReadings } from '../../hooks/useDeviceReadings'
import { api } from '../../services'
import { toast } from 'sonner'

// Sub-type definitions for rainfall sensors
const RAINFALL_SUB_TYPES = [
  { subType: 'amount' as const, label: 'Amount', unitCategoryId: UnitCategoryId.Rainfall },
  { subType: 'rate' as const, label: 'Rate', unitCategoryId: UnitCategoryId.RainfallRate },
  { subType: 'total' as const, label: 'Total', unitCategoryId: UnitCategoryId.Rainfall },
]

// Sub-type definitions for flow meter sensors
const FLOW_METER_SUB_TYPES = [
  { subType: 'volume' as const, label: 'Volume', unitCategoryId: UnitCategoryId.Volume },
  { subType: 'volumeRate' as const, label: 'Flow Rate', unitCategoryId: UnitCategoryId.VolumeRate },
  { subType: 'totalVolume' as const, label: 'Total Volume', unitCategoryId: UnitCategoryId.TotalVolume },
]

export function ExportModal({
  isOpen,
  onClose,
  device,
  currentTimeRange,
  sensorConfigs,
  readingsData,
  batteryData,
  devices
}: ExportModalProps) {
  const { userUnitSettings, unitConversions } = useAccountContext()

  // Multi-device mode state
  const [isMultiMode, setIsMultiMode] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set())
  const [multiProgress, setMultiProgress] = useState<MultiExportProgress | null>(null)

  // State
  const [dateRange, setDateRange] = useState({
    from: currentTimeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
    to: currentTimeRange.to || Date.now()
  })

  // Fetch readings data for selected date range
  // Convert ms timestamps to seconds for API
  const fromSeconds = Math.round(dateRange.from / 1000)
  const toSeconds = Math.round(dateRange.to / 1000)

  const {
    data: fetchedReadingsData,
    isFetching: isReadingsFetching
  } = useDeviceReadings(device.sigfoxId, fromSeconds, toSeconds, 50000, isOpen)

  const {
    data: fetchedBatteryData,
    isFetching: isBatteryFetching
  } = useBatteryReadings(device.sigfoxId, fromSeconds, toSeconds, 50000, isOpen)

  // Use fetched data (falls back to prop data if fetch not complete)
  const activeReadingsData = fetchedReadingsData || readingsData
  const activeBatteryData = fetchedBatteryData || batteryData
  const isDataLoading = isReadingsFetching || isBatteryFetching

  const [selectedSensors, setSelectedSensors] = useState<Map<string, SensorSelection>>(new Map())
  const [filename, setFilename] = useState('')
  const [timestampFormat, setTimestampFormat] = useState<TimestampFormat>('iso')
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState<ProgressState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flowMeterMeta, setFlowMeterMeta] = useState<Record<string, FlowMeterMeta>>({})

  // Fetch flow meter config (K-Value, Diameter, Area) when modal opens
  useEffect(() => {
    if (!isOpen || !sensorConfigs || !device?.sigfoxId) return

    const flowPorts: string[] = []
    for (const [port, config] of Object.entries(sensorConfigs)) {
      const productName = (config as any).sensorProductName || ''
      if (isFlowMeterProduct(productName)) {
        flowPorts.push(port)
      }
    }

    if (flowPorts.length === 0) return

    api.getSensorConfig(device.sigfoxId, Math.floor(Date.now() / 1000))
      .then((result) => {
        if (!result?.data) return
        const meta: Record<string, FlowMeterMeta> = {}
        for (const port of flowPorts) {
          const config = result.data[port]
          if (config) {
            meta[port] = {
              kValue: config.conversionScale,
              diameter: config.conversionOffset,
              area: config.areaValue ?? 100,
              productName: (sensorConfigs[port] as any)?.sensorProductName,
            }
          }
        }
        setFlowMeterMeta(meta)
      })
      .catch((err) => console.error('Failed to fetch flow meter config:', err))
  }, [isOpen, device?.sigfoxId, sensorConfigs])

  // Build sensor configs list (includes T0 temperature sensor and BAT)
  // Rainfall and flow meter sensors are expanded into 3 sub-type rows each
  const sensors = useMemo((): ExportSensorConfig[] => {
    if (!sensorConfigs) return []

    const sensorList: ExportSensorConfig[] = []
    const conversions = unitConversions as Record<number, any> | undefined
    const processedFlowPorts = new Set<string>()

    for (const [port, config] of Object.entries(sensorConfigs)) {
      const rawConfig = config as any
      const productName = port === 'T0'
        ? 'Internal Temperature'
        : (rawConfig.sensorProductName || 'Unknown Sensor')

      // Check if this sensor has data in the readings
      const sensorKey = Object.keys(activeReadingsData?.sensors || {})
        .find(key => key.startsWith(port + '-'))
      const sensorData = sensorKey ? activeReadingsData?.sensors[sensorKey] : null
      const hasData = !!(sensorData?.data?.length || Object.keys(sensorData?.readings || {}).length)
      const readingCount = sensorData?.data?.length || Object.keys(sensorData?.readings || {}).length || 0

      // Expand rainfall sensors into 3 sub-types
      if (isRainfallProduct(productName)) {
        for (const sub of RAINFALL_SUB_TYPES) {
          const compositePort = `${port}:${sub.subType}`
          const userUnit = userUnitSettings?.[sub.unitCategoryId]
          const unitId = userUnit?.unitId || sensorData?.metadata?.units?.id || 0
          const unitSymbol = conversions?.[sub.unitCategoryId]?.abbreviations?.[unitId] ||
                            sensorData?.metadata?.units?.symbol || ''

          sensorList.push({
            port: compositePort,
            name: rawConfig.sensorName || port,
            productName,
            unitCategoryId: sub.unitCategoryId,
            unitId,
            unitSymbol,
            hasData,
            readingCount,
            subType: sub.subType,
            parentPort: port,
            isSubRow: true,
            subTypeLabel: sub.label,
          })
        }
        continue
      }

      // Expand flow meter sensors into 3 sub-types (deduplicate across API keys)
      if (isFlowMeterProduct(productName)) {
        if (processedFlowPorts.has(port)) continue
        processedFlowPorts.add(port)

        for (const sub of FLOW_METER_SUB_TYPES) {
          const compositePort = `${port}:${sub.subType}`
          const userUnit = userUnitSettings?.[sub.unitCategoryId]
          const unitId = userUnit?.unitId || 0
          const unitSymbol = conversions?.[sub.unitCategoryId]?.abbreviations?.[unitId] || ''

          sensorList.push({
            port: compositePort,
            name: rawConfig.sensorName || port,
            productName,
            unitCategoryId: sub.unitCategoryId,
            unitId,
            unitSymbol,
            hasData,
            readingCount,
            subType: sub.subType,
            parentPort: port,
            isSubRow: true,
            subTypeLabel: sub.label,
          })
        }
        continue
      }

      // Standard sensor (non-expandable)
      const unitCategoryId: number = port === 'T0'
        ? Number(sensorData?.metadata?.units?.category?.id || UnitCategoryId.Temperature)
        : Number(rawConfig.unitCategoryId || sensorData?.metadata?.units?.category?.id || 0)
      const userUnit = userUnitSettings?.[unitCategoryId]
      const unitId = userUnit?.unitId || sensorData?.metadata?.units?.id || 0
      const unitSymbol = conversions?.[unitCategoryId]?.abbreviations?.[unitId] ||
                        sensorData?.metadata?.units?.symbol || (port === 'T0' ? '°C' : '')

      sensorList.push({
        port,
        name: rawConfig.sensorName || port,
        productName,
        unitCategoryId,
        unitId,
        unitSymbol,
        hasData,
        readingCount,
      })
    }

    // Always add T0 if not already in sensorConfigs
    const hasT0 = sensorList.some(s => s.port === 'T0' || s.parentPort === 'T0')
    if (!hasT0) {
      const t0Key = Object.keys(activeReadingsData?.sensors || {})
        .find(key => key.startsWith('T0-'))
      const t0Data = t0Key ? activeReadingsData?.sensors[t0Key] : null
      const t0HasData = !!(t0Data?.data?.length || Object.keys(t0Data?.readings || {}).length)
      const t0ReadingCount = t0Data?.data?.length || Object.keys(t0Data?.readings || {}).length || 0

      const tempCategoryId = UnitCategoryId.Temperature
      const userTempUnit = userUnitSettings?.[tempCategoryId]
      const tempUnitId = userTempUnit?.unitId || t0Data?.metadata?.units?.id || 0
      const tempUnitSymbol = (unitConversions as Record<number, any>)?.[tempCategoryId]?.abbreviations?.[tempUnitId] ||
                            t0Data?.metadata?.units?.symbol || '°C'

      sensorList.push({
        port: 'T0',
        name: 'T0',
        productName: 'Internal Temperature',
        unitCategoryId: tempCategoryId,
        unitId: tempUnitId,
        unitSymbol: tempUnitSymbol,
        hasData: t0HasData,
        readingCount: t0ReadingCount
      })
    }

    // Sort: regular ports first (A1, A2, B1...), then T0 last
    // Sub-type ports like "A1:volume" sort with their parent
    return sensorList.sort((a, b) => {
      const aBase = a.parentPort || a.port
      const bBase = b.parentPort || b.port
      if (aBase === 'T0' && bBase !== 'T0') return 1
      if (bBase === 'T0' && aBase !== 'T0') return -1
      if (aBase !== bBase) return aBase.localeCompare(bBase)
      return a.port.localeCompare(b.port)
    })
  }, [sensorConfigs, activeReadingsData, userUnitSettings, unitConversions])

  // Build available units map
  const availableUnits = useMemo(() => {
    const result: Record<number, Record<number, string>> = {}
    const conversions = unitConversions as Record<number, any> | undefined

    for (const sensor of sensors) {
      const category = conversions?.[sensor.unitCategoryId]
      if (category?.abbreviations) {
        result[sensor.unitCategoryId] = category.abbreviations
      }
    }

    return result
  }, [sensors, unitConversions])

  // Initialize selected sensors when modal opens or sensors change
  useEffect(() => {
    if (!isOpen) return

    const initial = new Map<string, SensorSelection>()
    for (const sensor of sensors) {
      initial.set(sensor.port, {
        port: sensor.port,
        selected: sensor.hasData,  // Pre-select sensors with data
        unitId: sensor.unitId,
        unitSymbol: sensor.unitSymbol
      })
    }
    setSelectedSensors(initial)
  }, [isOpen, sensors])

  // Initialize date range and filename when modal opens
  useEffect(() => {
    if (!isOpen) return

    setDateRange({
      from: currentTimeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
      to: currentTimeRange.to || Date.now()
    })

    setFilename(generateDefaultFilename(
      device.deviceName || device.sigfoxId,
      currentTimeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
      currentTimeRange.to || Date.now()
    ))

    setError(null)
    setProgress(null)
    setIsExporting(false)
    setIsMultiMode(false)
    setSelectedDevices(new Set([device.sigfoxId]))
    setMultiProgress(null)
  }, [isOpen, currentTimeRange, device])

  // Update filename when date range changes
  useEffect(() => {
    setFilename(generateDefaultFilename(
      device.deviceName || device.sigfoxId,
      dateRange.from,
      dateRange.to
    ))
  }, [dateRange, device])

  // Count selected sensors
  const selectedCount = useMemo(() => {
    let count = 0
    for (const selection of selectedSensors.values()) {
      if (selection.selected) count++
    }
    return count
  }, [selectedSensors])

  // Estimate reading count
  const estimatedReadings = useMemo(() => {
    if (!activeReadingsData?.sensors) return 0

    const selectedPorts = new Set<string>()
    for (const [port, selection] of selectedSensors) {
      if (selection.selected) selectedPorts.add(port)
    }

    const sensorReadings = transformSensorData(activeReadingsData.sensors, selectedPorts)
    return estimateReadingCount(
      sensorReadings,
      Math.round(dateRange.from / 1000),
      Math.round(dateRange.to / 1000)
    )
  }, [activeReadingsData, selectedSensors, dateRange])

  // Handle selection changes
  const handleSelectionChange = useCallback((port: string, selected: boolean) => {
    setSelectedSensors(prev => {
      const next = new Map(prev)
      const current = next.get(port)
      if (current) {
        next.set(port, { ...current, selected })
      }
      return next
    })
  }, [])

  const handleUnitChange = useCallback((port: string, unitId: number, unitSymbol: string) => {
    setSelectedSensors(prev => {
      const next = new Map(prev)
      const current = next.get(port)
      if (current) {
        next.set(port, { ...current, unitId, unitSymbol })
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback((selected: boolean) => {
    setSelectedSensors(prev => {
      const next = new Map(prev)
      for (const [port, selection] of next) {
        next.set(port, { ...selection, selected })
      }
      return next
    })
  }, [])

  const handleDateRangeChange = useCallback((from: number, to: number) => {
    setDateRange({ from, to })
  }, [])

  const handleFilenameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Sanitize filename
    const value = e.target.value
      .replace(/[<>:"/\\|?*]/g, '')  // Remove invalid chars
      .replace(/\.csv$/i, '')        // Remove .csv extension if typed
    setFilename(value)
  }, [])

  const handleReset = useCallback(() => {
    // Reset to defaults
    setDateRange({
      from: currentTimeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
      to: currentTimeRange.to || Date.now()
    })

    // Re-initialize selected sensors
    const initial = new Map<string, SensorSelection>()
    for (const sensor of sensors) {
      initial.set(sensor.port, {
        port: sensor.port,
        selected: sensor.hasData,
        unitId: sensor.unitId,
        unitSymbol: sensor.unitSymbol
      })
    }
    setSelectedSensors(initial)

    setFilename(generateDefaultFilename(
      device.deviceName || device.sigfoxId,
      currentTimeRange.from || Date.now() - 28 * 24 * 60 * 60 * 1000,
      currentTimeRange.to || Date.now()
    ))
  }, [currentTimeRange, sensors, device])

  // Multi-device mode handlers
  const handleToggleMultiMode = useCallback(() => {
    setIsMultiMode(prev => !prev)
  }, [])

  const handleDeviceSelectionChange = useCallback((sigfoxId: string, selected: boolean) => {
    setSelectedDevices(prev => {
      const next = new Set(prev)
      if (selected) {
        next.add(sigfoxId)
      } else {
        next.delete(sigfoxId)
      }
      return next
    })
  }, [])

  const handleDeviceSelectAll = useCallback((selected: boolean) => {
    if (!devices) return
    if (selected) {
      setSelectedDevices(new Set(Object.keys(devices)))
    } else {
      setSelectedDevices(new Set())
    }
  }, [devices])

  // Handle export
  const handleExport = useCallback(async () => {
    if (!activeReadingsData?.sensors || selectedCount === 0) return

    setIsExporting(true)
    setError(null)
    setProgress({
      phase: 'preparing',
      current: 0,
      total: 100,
      message: 'Preparing export...'
    })

    try {
      // Get selected ports and unit overrides
      const selectedPorts = new Set<string>()
      const unitOverrides = new Map<string, { unitId: number; unitSymbol: string }>()

      for (const [port, selection] of selectedSensors) {
        if (selection.selected) {
          selectedPorts.add(port)
          unitOverrides.set(port, {
            unitId: selection.unitId,
            unitSymbol: selection.unitSymbol
          })
        }
      }

      // Transform sensor data with sub-type expansion for rain/flow sensors
      const sensorReadings = transformSensorData(
        activeReadingsData.sensors,
        selectedPorts,
        unitOverrides,
        activeBatteryData,
        true // expandSubTypes
      )

      // Build column headers with sub-type labels
      const columns: CsvColumn[] = sensorReadings.map(sensor => {
        // Find sub-type label from sensors config
        const sensorConfig = sensors.find(s => s.port === sensor.port)
        const subTypeLabel = sensorConfig?.subTypeLabel
        return {
          port: sensor.port,
          sensorName: sensor.productName,
          unitSymbol: sensor.unitSymbol,
          header: generateColumnHeader(sensor.port, sensor.productName, sensor.unitSymbol, subTypeLabel)
        }
      })

      // Build extended metadata
      const exportMetadata = {
        deviceName: device.deviceName || device.sigfoxId,
        sigfoxId: device.sigfoxId,
        siteName: device.siteName || '',
        dateRange: {
          from: new Date(dateRange.from),
          to: new Date(dateRange.to)
        },
        exportedAt: new Date(),
        serialNumber: device.serialNumber,
        hardwareVersion: device.hardwareNumber !== undefined
          ? parseHardwareVersion(device.hardwareNumber)
          : undefined,
        firmwareVersion: device.firmwareVersion !== undefined
          ? parseFirmwareVersion(device.firmwareVersion)
          : undefined,
        wisolId: device.wisolId,
        latitude: device.coordinateNSValue,
        longitude: device.coordinateEWValue,
        elevation: device.coordinateZValue,
        sensorMeta: Object.keys(flowMeterMeta).length > 0 ? flowMeterMeta : undefined,
      }

      // Generate CSV
      const csvContent = await generateCsvExport(
        exportMetadata,
        sensorReadings,
        columns,
        unitConversions || {},
        setProgress,
        timestampFormat
      )

      // Download file
      const result = downloadCsvFile(csvContent, filename)

      if (result.success) {
        toast.success(`Exported ${result.rowCount?.toLocaleString() || 0} readings to ${result.filename}`)
        onClose()
      } else {
        throw new Error(result.error || 'Failed to download file')
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed'
      setError(message)
      toast.error(message)
    } finally {
      setIsExporting(false)
      setProgress(null)
    }
  }, [
    activeReadingsData,
    activeBatteryData,
    selectedSensors,
    selectedCount,
    device,
    dateRange,
    filename,
    timestampFormat,
    unitConversions,
    onClose,
    sensors,
    flowMeterMeta,
  ])

  // Handle multi-device export
  const handleMultiExport = useCallback(async () => {
    if (!devices || selectedDevices.size === 0 || selectedCount === 0) return

    setIsExporting(true)
    setError(null)

    // Build selected ports and unit overrides
    const selectedPorts = new Set<string>()
    const unitOverrides = new Map<string, { unitId: number; unitSymbol: string }>()

    for (const [port, selection] of selectedSensors) {
      if (selection.selected) {
        selectedPorts.add(port)
        unitOverrides.set(port, {
          unitId: selection.unitId,
          unitSymbol: selection.unitSymbol
        })
      }
    }

    // Initialize multi-progress state
    const deviceIds = Array.from(selectedDevices)
    const initialStatuses: Record<string, DeviceExportStatus> = {}
    for (const sigfoxId of deviceIds) {
      const deviceInfo = devices[sigfoxId]
      initialStatuses[sigfoxId] = {
        sigfoxId,
        deviceName: deviceInfo?.deviceName || sigfoxId,
        status: 'pending'
      }
    }

    setMultiProgress({
      totalDevices: deviceIds.length,
      completedDevices: 0,
      currentDeviceId: null,
      deviceStatuses: initialStatuses
    })

    let completedCount = 0
    const fromSeconds = Math.round(dateRange.from / 1000)
    const toSeconds = Math.round(dateRange.to / 1000)

    // Process each device sequentially - same process as single device export
    for (let i = 0; i < deviceIds.length; i++) {
      const sigfoxId = deviceIds[i]
      const deviceInfo = devices[sigfoxId]
      if (!deviceInfo) continue

      const deviceName = deviceInfo.deviceName || sigfoxId

      // Update status to fetching
      setMultiProgress(prev => prev ? {
        ...prev,
        currentDeviceId: sigfoxId,
        deviceStatuses: {
          ...prev.deviceStatuses,
          [sigfoxId]: { ...prev.deviceStatuses[sigfoxId], status: 'fetching' }
        }
      } : null)

      try {
        // Fetch readings for this device
        console.log(`📥 [${i + 1}/${deviceIds.length}] Fetching readings for ${deviceName}...`)
        const apiResponse = await api.getReadings(sigfoxId, fromSeconds, toSeconds, 50000)

        // API returns { data: { "A1-13": {...}, "B2-12": {...} } }
        const deviceSensors = apiResponse?.data || apiResponse || {}
        const sensorCount = Object.keys(deviceSensors).length
        console.log(`📦 Found ${sensorCount} sensors for ${deviceName}`)

        // Update status to generating
        setMultiProgress(prev => prev ? {
          ...prev,
          deviceStatuses: {
            ...prev.deviceStatuses,
            [sigfoxId]: { ...prev.deviceStatuses[sigfoxId], status: 'generating' }
          }
        } : null)

        // Transform sensor data with sub-type expansion (may be empty - that's OK)
        const sensorReadings = sensorCount > 0
          ? transformSensorData(deviceSensors, selectedPorts, unitOverrides, undefined, true)
          : []

        // Build column headers with sub-type labels
        const columns: CsvColumn[] = sensorReadings.map(sensor => {
          const sensorConfig = sensors.find(s => s.port === sensor.port)
          const subTypeLabel = sensorConfig?.subTypeLabel
          return {
            port: sensor.port,
            sensorName: sensor.productName,
            unitSymbol: sensor.unitSymbol,
            header: generateColumnHeader(sensor.port, sensor.productName, sensor.unitSymbol, subTypeLabel)
          }
        })

        // Generate CSV (will have just headers if no data)
        const csvContent = await generateCsvExport(
          {
            deviceName: deviceName,
            sigfoxId: sigfoxId,
            siteName: deviceInfo.siteName || '',
            dateRange: {
              from: new Date(dateRange.from),
              to: new Date(dateRange.to)
            },
            exportedAt: new Date(),
            serialNumber: deviceInfo.serialNumber,
            hardwareVersion: deviceInfo.hardwareNumber,
            firmwareVersion: deviceInfo.firmwareVersion,
            wisolId: deviceInfo.wisolId,
            latitude: deviceInfo.coordinateNSValue,
            longitude: deviceInfo.coordinateEWValue,
            elevation: deviceInfo.coordinateZValue,
          },
          sensorReadings,
          columns,
          unitConversions || {},
          undefined,
          timestampFormat
        )

        // Generate filename for this device
        const deviceFilename = generateDefaultFilename(deviceName, dateRange.from, dateRange.to)

        // Download file with proper timing
        console.log(`💾 Downloading ${deviceFilename}.csv...`)

        // Create download with delayed cleanup to ensure browser processes it
        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${deviceFilename}.csv`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        // Count rows for toast
        const rowCount = csvContent.split('\n').filter(line =>
          !line.startsWith('#') && line.trim() !== ''
        ).length - 1

        // Show individual toast for this device
        toast.success(`${deviceName}: ${Math.max(0, rowCount).toLocaleString()} readings exported`)

        // Cleanup after delay
        await new Promise(resolve => setTimeout(resolve, 100))
        document.body.removeChild(link)
        URL.revokeObjectURL(url)

        console.log(`✅ Downloaded ${deviceFilename}.csv with ${rowCount} rows`)

        // Update status to complete
        completedCount++
        const completedSoFar = completedCount
        setMultiProgress(prev => prev ? {
          ...prev,
          completedDevices: completedSoFar,
          deviceStatuses: {
            ...prev.deviceStatuses,
            [sigfoxId]: { ...prev.deviceStatuses[sigfoxId], status: 'complete' }
          }
        } : null)

        // Longer delay between downloads to ensure browser processes each one
        if (i < deviceIds.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Export failed'
        console.error(`❌ Export failed for ${deviceName}:`, errorMessage)
        toast.error(`${deviceName}: Export failed`)
        const completedSoFar = completedCount
        setMultiProgress(prev => prev ? {
          ...prev,
          completedDevices: completedSoFar,
          deviceStatuses: {
            ...prev.deviceStatuses,
            [sigfoxId]: { ...prev.deviceStatuses[sigfoxId], status: 'error', error: errorMessage }
          }
        } : null)
      }
    }

    // Final summary (only if multiple devices)
    if (deviceIds.length > 1) {
      const failedCount = deviceIds.length - completedCount
      if (failedCount === 0) {
        toast.success(`All ${completedCount} exports complete`)
      } else {
        toast.warning(`${completedCount} of ${deviceIds.length} devices exported`)
      }
    }

    setIsExporting(false)
    setMultiProgress(null)
  }, [
    devices,
    selectedDevices,
    selectedSensors,
    selectedCount,
    sensors,
    dateRange,
    timestampFormat,
    unitConversions
  ])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isExporting) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, isExporting, onClose])

  if (!isOpen) return null

  // Determine export button state and text
  const canExport = isMultiMode
    ? selectedDevices.size > 0 && selectedCount > 0
    : selectedCount > 0

  const exportButtonText = isMultiMode
    ? `Export ${selectedDevices.size} Device${selectedDevices.size !== 1 ? 's' : ''}`
    : `Export ${selectedCount} Sensor${selectedCount !== 1 ? 's' : ''}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className={cn(
        'bg-white rounded-lg shadow-xl w-full mx-4 overflow-hidden flex flex-col transition-all duration-200',
        isMultiMode ? 'max-w-5xl h-[85vh]' : 'max-w-xl max-h-[90vh]'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Export Sensor Data</h2>
            <p className="text-sm text-gray-500">
              {isMultiMode
                ? 'Export multiple devices to separate CSV files'
                : 'Download readings as CSV file'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className={cn(
              'p-2 rounded-md transition-colors',
              isExporting
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            )}
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className={cn(
          'flex-1 overflow-hidden min-h-0',
          isMultiMode ? 'flex' : 'overflow-y-auto'
        )}>
          {/* Device Selection Panel (Multi-mode only) */}
          {isMultiMode && devices && (
            <div className="w-80 shrink-0 border-r border-gray-200 px-5 py-5 flex flex-col bg-gray-50/30">
              <ExportDeviceSelection
                devices={devices}
                selectedDevices={selectedDevices}
                currentDeviceId={device.sigfoxId}
                onSelectionChange={handleDeviceSelectionChange}
                onSelectAll={handleDeviceSelectAll}
              />
            </div>
          )}

          {/* Main Content */}
          <div className={cn(
            'flex-1 px-5 py-5 space-y-5 overflow-y-auto',
            isMultiMode && 'min-w-0'
          )}>
            {/* Date Range */}
            <ExportDateRange
              from={dateRange.from}
              to={dateRange.to}
              onChange={handleDateRangeChange}
            />

            {/* Date Format - below date range */}
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Date Format
              </label>
              <div className="flex items-center gap-3">
                <div className="relative w-56">
                  <select
                    value={timestampFormat}
                    onChange={(e) => setTimestampFormat(e.target.value as TimestampFormat)}
                    disabled={isExporting}
                    className={cn(
                      'w-full appearance-none px-3 py-2 pr-8 text-sm border border-gray-200 rounded-md',
                      'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
                      'bg-white',
                      isExporting && 'bg-gray-100'
                    )}
                  >
                    {TIMESTAMP_FORMATS.map((fmt) => (
                      <option key={fmt.value} value={fmt.value}>
                        {fmt.label}
                      </option>
                    ))}
                  </select>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-500 pointer-events-none"
                  />
                </div>
                <span className="text-xs text-gray-400">
                  {TIMESTAMP_FORMATS.find(f => f.value === timestampFormat)?.example}
                </span>
              </div>
            </div>

            {/* Sensor Selection */}
            <ExportSensorSelection
              sensors={sensors}
              selectedSensors={selectedSensors}
              onSelectionChange={handleSelectionChange}
              onUnitChange={handleUnitChange}
              onSelectAll={handleSelectAll}
              availableUnits={availableUnits}
            />

            {/* Filename - only shown in single device mode */}
            {!isMultiMode && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Filename
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={filename}
                    onChange={handleFilenameChange}
                    disabled={isExporting}
                    className={cn(
                      'flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md',
                      'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
                      isExporting && 'bg-gray-100'
                    )}
                    placeholder="export-filename"
                  />
                  <span className="text-sm text-gray-500">.csv</span>
                </div>
              </div>
            )}

            {/* Filename note for multi-mode */}
            {isMultiMode && (
              <div className="text-xs text-gray-500 italic">
                Each device will be exported to a separate file named: DeviceName___YYYY-MM-DD_to_YYYY-MM-DD.csv
              </div>
            )}

            {/* Progress (single device mode) */}
            {progress && !isMultiMode && (
              <ExportProgress progress={progress} />
            )}

            {/* Multi-device progress */}
            {multiProgress && isMultiMode && (
              <ExportMultiProgress progress={multiProgress} />
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <ExportSummary
            selectedSensorCount={selectedCount}
            estimatedReadings={isMultiMode ? estimatedReadings * selectedDevices.size : estimatedReadings}
            isLoading={isDataLoading}
            deviceCount={isMultiMode ? selectedDevices.size : undefined}
          />

          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                disabled={isExporting}
                className={cn(
                  'text-sm text-gray-500 hover:text-gray-700',
                  isExporting && 'opacity-50 cursor-not-allowed'
                )}
              >
                Reset to defaults
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Multi-device toggle button - only show if devices available */}
              {devices && Object.keys(devices).length > 1 && (
                <button
                  onClick={handleToggleMultiMode}
                  disabled={isExporting}
                  className={cn(
                    'px-3 py-2 text-sm font-medium rounded-md border',
                    'flex items-center gap-2',
                    isMultiMode
                      ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                    isExporting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4" />
                  {isMultiMode ? 'Single Device' : 'Export Multiple'}
                </button>
              )}

              <button
                onClick={onClose}
                disabled={isExporting}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md border border-gray-300',
                  'bg-white text-gray-700 hover:bg-gray-50',
                  isExporting && 'opacity-50 cursor-not-allowed'
                )}
              >
                Cancel
              </button>
              <button
                onClick={isMultiMode ? handleMultiExport : handleExport}
                disabled={isExporting || !canExport}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-md',
                  'flex items-center gap-2',
                  canExport && !isExporting
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                {isExporting ? 'Exporting...' : exportButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
